import { useEffect, useRef, useState } from "react";
import { Play, Loader2, Video } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { useWorkspace } from "../../store/WorkspaceContext";
import { useAuth } from "../../store/AuthContext";
import { api } from "../../lib/api";

const SESSION_KEY = "xpattern.introVideo.shown";

// Toolbar "Watch Video" → the owner's walkthrough, paused with a big Play button until the tinter presses it.
export const WatchVideoDialog = () => {
  const { state, dispatch } = useWorkspace();
  const { user } = useAuth();
  const open = state.dialog === "video";
  const [info, setInfo] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [auto, setAuto] = useState(false);
  const [dontShow, setDontShow] = useState(false);
  const ref = useRef(null);
  const watch = useRef({ played: false, pct: 0 });  // what this open achieved — reported once on close for the owner's stats
  const onTime = () => { const v = ref.current; if (v?.duration) watch.current.pct = Math.max(watch.current.pct, (v.currentTime / v.duration) * 100); };
  useEffect(() => {
    if (!open) { setPlaying(false); return; }
    watch.current = { played: false, pct: 0 };
    setInfo(null);
    api.introVideo().then(setInfo).catch(() => setInfo({ available: false }));
  }, [open]);
  // first sign-in of this session: pop the walkthrough up for customers who haven't ticked "Don't show this again"
  useEffect(() => {
    if (!user || user.role === "admin" || sessionStorage.getItem(SESSION_KEY)) return;
    let alive = true;
    api.introVideo().then((i) => {
      if (!alive || !i.show_on_login) return;
      sessionStorage.setItem(SESSION_KEY, "1");
      setAuto(true); setDontShow(false);
      dispatch({ type: "SET", patch: { dialog: "video" } });
    }).catch(() => {});
    return () => { alive = false; };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const close = () => {
    if (info?.available && user?.role !== "admin") api.introVideoView({ ...watch.current, auto }).catch(() => {});
    if (auto && dontShow) api.introVideoDismiss().catch(() => {});
    setAuto(false);
    dispatch({ type: "SET", patch: { dialog: state.videoFrom || null, videoFrom: null } });  // back to the dashboard when opened from there
  };
  const play = () => { ref.current?.play(); setPlaying(true); watch.current.played = true; };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="panel3d max-w-4xl border-white/10 bg-[#0f141d] p-0 text-[#e8e9ec]" data-testid="watch-video-dialog">
        <DialogHeader className="px-5 pt-4">
          <DialogTitle className="flex items-center gap-2 text-[15px]" data-testid="watch-video-title"><Video className="h-4 w-4 text-[#ff8a8e]" /> {info?.title || "Watch the Xpattern walkthrough"}</DialogTitle>
          <DialogDescription className="text-[12px] text-[#9aa0a8]">{auto ? "Welcome to Xpattern — a quick walkthrough before you start. " : ""}Press play — you can go full screen from the player controls.</DialogDescription>
        </DialogHeader>
        <div className="relative mx-5 mb-5 aspect-video overflow-hidden rounded-lg border border-white/10 bg-black">
          {!info && <div className="flex h-full items-center justify-center text-[#9aa0a8]"><Loader2 className="h-6 w-6 animate-spin" /></div>}
          {info && !info.available && (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-center" data-testid="watch-video-empty">
              <Video className="h-10 w-10 text-[#3a4150]" />
              <div className="text-[14px] font-semibold text-white">Video coming soon</div>
              <div className="max-w-sm text-[12px] text-[#9aa0a8]">The walkthrough hasn't been uploaded yet. Check back shortly.</div>
            </div>
          )}
          {info?.available && (
            <>
              <video ref={ref} data-testid="watch-video-player" src={api.introVideoUrl()} controls={playing} preload="metadata" playsInline className="h-full w-full bg-black" onPlay={() => { setPlaying(true); watch.current.played = true; }} onTimeUpdate={onTime} onEnded={() => { watch.current.pct = 100; }} onPause={() => { if (ref.current?.ended) setPlaying(false); }} />
              {!playing && (
                <button data-testid="watch-video-play-btn" onClick={play} className="group absolute inset-0 flex items-center justify-center bg-black/40 transition-colors hover:bg-black/30" aria-label="Play video">
                  <span className="flex h-20 w-20 items-center justify-center rounded-full bg-[#e5484d] shadow-[0_10px_40px_-10px_rgba(229,72,77,.9)] transition-transform group-hover:scale-105"><Play className="ml-1.5 h-9 w-9 fill-white text-white" /></span>
                </button>
              )}
            </>
          )}
        </div>
        {auto && (
          <div className="flex items-center justify-between gap-3 border-t border-white/10 px-5 py-3" data-testid="watch-video-auto-footer">
            <label className="flex cursor-pointer items-center gap-2 text-[12px] text-[#b4b9c1]">
              <input data-testid="watch-video-dont-show" type="checkbox" checked={dontShow} onChange={(e) => setDontShow(e.target.checked)} className="h-3.5 w-3.5 accent-[#e5484d]" />
              Don't show this again
            </label>
            <button data-testid="watch-video-close-btn" onClick={close} className="btn px-4 py-1.5 text-[12px]">{dontShow ? "Got it, close" : "Skip for now"}</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
